import { Button } from '@personachat/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Check, X } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { api } from '../../api/client';
import { lire } from '../../api/call';

import { etapesPour, placerBulle, type EtapeAffichee, type Rectangle } from './etapes';

/**
 * Visite guidée du back-office : se lance d'elle-même à la première connexion
 * d'un admin (tant que l'API ne l'a pas notée vue), et se relance depuis
 * « Mon compte » (useVisite().lancer).
 */

const ContexteVisite = createContext<{ lancer: () => void } | null>(null);

export function useVisite() {
  const ctx = useContext(ContexteVisite);
  if (!ctx) throw new Error('useVisite() hors de VisiteProvider');
  return ctx;
}

export function VisiteProvider({ superAdmin, children }: { superAdmin: boolean; children: ReactNode }) {
  const client = useQueryClient();
  const [ouverte, setOuverte] = useState(false);
  const lanceeSeule = useRef(false);

  const etat = useQuery({
    queryKey: ['visite-guidee'],
    queryFn: () => lire(api.GET('/api/auth/visite-guidee')),
    staleTime: Infinity,
    retry: false,
  });
  const marquerVue = useMutation({
    mutationFn: () => lire(api.POST('/api/auth/visite-guidee')),
    onSuccess: (d) => client.setQueryData(['visite-guidee'], d),
  });

  // Première connexion : une seule fois par chargement, même si l'enregistrement échoue.
  useEffect(() => {
    if (lanceeSeule.current || !etat.data || etat.data.seenAt) return;
    lanceeSeule.current = true;
    setOuverte(true);
  }, [etat.data]);

  const lancer = useCallback(() => setOuverte(true), []);
  const fermer = useCallback(() => {
    setOuverte(false);
    marquerVue.mutate();
  }, [marquerVue]);

  const valeur = useMemo(() => ({ lancer }), [lancer]);
  return (
    <ContexteVisite.Provider value={valeur}>
      {children}
      {ouverte && <VisiteGuidee etapes={etapesPour(superAdmin)} onFermer={fermer} />}
    </ContexteVisite.Provider>
  );
}

const RETRAIT = 6;
const LARGEUR_BULLE = 360;
const PETIT_ECRAN = 640;

function rectangleDe(cible: string | undefined): Rectangle | null {
  if (!cible) return null;
  const el = document.querySelector<HTMLElement>(`[data-tour="${cible}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 || r.height === 0) return null; // masqué (petit écran, barre repliée…)
  return { top: r.top - RETRAIT, left: r.left - RETRAIT, width: r.width + RETRAIT * 2, height: r.height + RETRAIT * 2 };
}

export function VisiteGuidee({ etapes, onFermer }: { etapes: EtapeAffichee[]; onFermer: () => void }) {
  const [index, setIndex] = useState(0);
  const [cible, setCible] = useState<Rectangle | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const bulle = useRef<HTMLDivElement>(null);
  const principal = useRef<HTMLButtonElement>(null);
  // Élément qui avait le focus avant l'ouverture (lu au premier rendu, avant que la bulle le prenne).
  const focusAvant = useRef(typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null);
  const titreId = useId();
  const texteId = useId();
  const etape = etapes[index]!;
  const derniere = index === etapes.length - 1;

  // Élément mis en avant : amené à l'écran, puis mesuré (et remesuré au redimensionnement).
  useLayoutEffect(() => {
    const el = etape.cible ? document.querySelector<HTMLElement>(`[data-tour="${etape.cible}"]`) : null;
    el?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    let image = 0;
    const mesurer = () => {
      cancelAnimationFrame(image);
      image = requestAnimationFrame(() => setCible(rectangleDe(etape.cible)));
    };
    setCible(rectangleDe(etape.cible));
    window.addEventListener('resize', mesurer);
    window.addEventListener('scroll', mesurer, true);
    return () => {
      cancelAnimationFrame(image);
      window.removeEventListener('resize', mesurer);
      window.removeEventListener('scroll', mesurer, true);
    };
  }, [etape.cible]);

  // Bulle placée d'après sa taille réelle ; en bas de l'écran sur mobile (CSS).
  useLayoutEffect(() => {
    const b = bulle.current;
    if (!b || window.innerWidth < PETIT_ECRAN) {
      setPosition(null);
      return;
    }
    const p = placerBulle(cible, { width: b.offsetWidth || LARGEUR_BULLE, height: b.offsetHeight }, { width: window.innerWidth, height: window.innerHeight });
    setPosition({ top: p.top, left: p.left });
  }, [cible, index]);

  // Focus sur le bouton principal à chaque étape ; rendu à l'élément d'origine à la fermeture.
  useEffect(() => {
    principal.current?.focus();
  }, [index]);
  useEffect(() => {
    const avant = focusAvant.current;
    return () => avant?.focus?.();
  }, []);

  const suivante = () => (derniere ? onFermer() : setIndex((i) => i + 1));
  const precedente = () => setIndex((i) => Math.max(0, i - 1));

  const clavier = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onFermer();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      suivante();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      precedente();
    } else if (e.key === 'Tab') {
      // Le focus reste dans la bulle.
      const boutons = Array.from(bulle.current?.querySelectorAll<HTMLElement>('button:not([disabled])') ?? []);
      if (boutons.length === 0) return;
      const i = boutons.indexOf(document.activeElement as HTMLElement);
      const cibleFocus = e.shiftKey ? boutons[(i - 1 + boutons.length) % boutons.length] : boutons[(i + 1) % boutons.length];
      e.preventDefault();
      cibleFocus?.focus();
    }
  };

  return createPortal(
    <div className="visite" onKeyDown={clavier}>
      {/* Voile : bloque les clics sur la page pendant la visite. */}
      <div className={cible ? 'visite__voile' : 'visite__voile visite__voile--sombre'} aria-hidden="true" />
      {cible && (
        <div
          className="visite__halo"
          aria-hidden="true"
          style={{ top: cible.top, left: cible.left, width: cible.width, height: cible.height }}
        />
      )}
      <div
        ref={bulle}
        className={position ? 'visite__bulle' : 'visite__bulle visite__bulle--libre'}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titreId}
        aria-describedby={texteId}
        style={position ? { top: position.top, left: position.left } : undefined}
      >
        <div className="visite__entete">
          <span className="visite__progression">
            Étape {index + 1} sur {etapes.length}
          </span>
          <button type="button" className="visite__fermer" onClick={onFermer} aria-label="Passer la visite">
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <div aria-live="polite">
          <h2 id={titreId} className="visite__titre">
            {etape.titre}
          </h2>
          <p id={texteId} className="visite__texte">
            {etape.texte}
          </p>
        </div>
        <div className="visite__points" aria-hidden="true">
          {etapes.map((e, i) => (
            <span key={e.id} className={i === index ? 'visite__point visite__point--actif' : 'visite__point'} />
          ))}
        </div>
        <div className="visite__actions">
          {index === 0 ? (
            <Button size="sm" variant="ghost" onClick={onFermer}>
              Passer
            </Button>
          ) : (
            <Button size="sm" variant="ghost" icon={<ArrowLeft size={14} aria-hidden="true" />} onClick={precedente}>
              Précédent
            </Button>
          )}
          <Button
            ref={principal}
            size="sm"
            variant="primary"
            icon={derniere ? <Check size={14} aria-hidden="true" /> : <ArrowRight size={14} aria-hidden="true" />}
            onClick={suivante}
          >
            {index === 0 ? 'Commencer' : derniere ? 'Terminer' : 'Suivant'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
